import {
  EMPTY_READINESS_CHECK_PROMPT,
  ORCHESTRATION_ONBOARDING_TIP,
  READINESS_ACTION_LABELS,
  READINESS_CHECK_PRIMARY_LABEL,
  READINESS_CHECK_PRIMARY_SUBTITLE,
  REPORTING_HUB_HANDOFF,
} from '../constants/orchestrationUxLabels';

export type ReadinessLocale = 'en' | 'hi';

export const READINESS_LOCALE_STORAGE_KEY = 'ci_readiness_locale';

export const READINESS_LOCALE_OPTIONS: Array<{ id: ReadinessLocale; label: string }> = [
  { id: 'en', label: 'English' },
  { id: 'hi', label: 'हिंदी (Hindi)' },
];

type ReadinessLocaleStrings = {
  checkReadiness: string;
  checkReadinessSubtitle: string;
  updateActionList: string;
  autoFixFromDocuments: string;
  hubHandoff: string;
  emptyReadinessPrompt: string;
  languageToggleLabel: string;
  isoAssuranceComplete: string;
  isoAssuranceFailed: string;
  brsrDisclosureComplete: string;
  brsrDisclosureFailed: string;
  cbamReadinessComplete: string;
  cbamReadinessFailed: string;
  pcfOrchestrationFailed: string;
  exportHubHandoff: string;
  indiaHubHandoff: string;
  brsrScopeNotice: string;
  spendProxyWarning: string;
  onboardingTitle: string;
  onboardingSubtitle: string;
  onboardingDismiss: string;
  onboardingStartAssessment: string;
  onboardingUploadBills: string;
  onboardingSteps: string[];
  statutoryLockedHint: string;
  statutoryDownloadSuccess: string;
  fixNextStep: string;
  itemsLeftTitle: string;
  moreGapsTitle: string;
  fixNow: string;
  readinessRequired: string;
  expertModeOn: string;
  expertModeOff: string;
  expertModeHint: string;
  matchBillsAutomatically: string;
  checkReadinessFirst: string;
  prepareAllReports: string;
  orchestrationTipTitle: string;
  orchestrationTipSubtitle: string;
  orchestrationTipDismiss: string;
  orchestrationTipSteps: string[];
};

export const READINESS_LOCALE_STRINGS: Record<ReadinessLocale, ReadinessLocaleStrings> = {
  en: {
    checkReadiness: READINESS_CHECK_PRIMARY_LABEL,
    checkReadinessSubtitle: READINESS_CHECK_PRIMARY_SUBTITLE,
    updateActionList: READINESS_ACTION_LABELS.updateActionList,
    autoFixFromDocuments: READINESS_ACTION_LABELS.autoFixFromDocuments,
    hubHandoff: REPORTING_HUB_HANDOFF,
    emptyReadinessPrompt: EMPTY_READINESS_CHECK_PROMPT,
    languageToggleLabel: 'Language',
    isoAssuranceComplete: 'ISO readiness check finished. Review the action list for any remaining gaps.',
    isoAssuranceFailed: 'ISO readiness check could not finish. Try again or review your inventory data.',
    brsrDisclosureComplete: 'BRSR readiness check finished. Review disclosure gaps in the action list.',
    brsrDisclosureFailed: 'BRSR readiness check could not finish. Complete your carbon assessment first.',
    cbamReadinessComplete: 'CBAM export check finished. Review installation and documentation gaps.',
    cbamReadinessFailed: 'CBAM export check could not finish. Confirm your export profile and bills.',
    pcfOrchestrationFailed: 'Product footprint check could not finish. Add product rules and transactions.',
    exportHubHandoff: 'Open Compliance Hub to run a full readiness check and see your prioritized action list.',
    indiaHubHandoff: 'Open Compliance Hub to run a full readiness check and see your prioritized action list.',
    brsrScopeNotice: 'This report covers BRSR Principle 6 (environmental) only — not the full nine-principle BRSR Core filing. Use it for prep and value-chain disclosure, then extend with social and governance sections before statutory filing.',
    spendProxyWarning: 'Many transactions still use ₹-based estimates. Upload bills with kWh, litres, or kg readings to improve accuracy before assurance.',
    onboardingTitle: 'New to compliance reporting?',
    onboardingSubtitle: 'Follow these three steps — most MSMEs finish the basics in one working session.',
    onboardingDismiss: 'Got it',
    onboardingStartAssessment: 'Start carbon assessment',
    onboardingUploadBills: 'Upload utility bills',
    onboardingSteps: [
      'Baseline footprint',
      'Upload bills',
      'Run readiness check',
    ],
    statutoryLockedHint: '{format} unlocks after {requirement}.',
    statutoryDownloadSuccess: '{format} downloaded successfully.',
    fixNextStep: 'Fix next step',
    itemsLeftTitle: '{count} step(s) left for official files',
    moreGapsTitle: 'Other items to complete',
    fixNow: 'Fix now',
    readinessRequired: 'Readiness required',
    expertModeOn: 'Technical view',
    expertModeOff: 'Simple view',
    expertModeHint: 'For auditors, consultants, or export compliance teams.',
    matchBillsAutomatically: 'Match my bills automatically',
    checkReadinessFirst: 'Check readiness first',
    prepareAllReports: 'Prepare all my reports',
    orchestrationTipTitle: ORCHESTRATION_ONBOARDING_TIP.title,
    orchestrationTipSubtitle: ORCHESTRATION_ONBOARDING_TIP.subtitle,
    orchestrationTipDismiss: ORCHESTRATION_ONBOARDING_TIP.dismiss,
    orchestrationTipSteps: [...ORCHESTRATION_ONBOARDING_TIP.steps],
  },
  hi: {
    checkReadiness: 'मेरी रिपोर्टिंग तैयारी जांचें',
    checkReadinessSubtitle: 'आपके बिल, प्रकटीकरण फ़ील्ड और खरीदार अनुरोध स्कैन करता है · लगभग 1 मिनट',
    updateActionList: 'कार्य सूची अपडेट करें',
    autoFixFromDocuments: 'मेरे बिल और दस्तावेज़ से ऑटो-फ़िक्स',
    hubHandoff: 'प्राथमिकता वाले अगले कदम और एक-क्लिक तैयारी जांच के लिए Compliance Hub खोलें — भारत और निर्यात रिपोर्टिंग का कमांड सेंटर।',
    emptyReadinessPrompt: 'आपने अभी तक तैयारी जांच नहीं चलाई है। कम से कम 2–3 बिजली या ईंधन बिल अपलोड करें, फिर "मेरी रिपोर्टिंग तैयारी जांचें" दबाएँ।',
    languageToggleLabel: 'भाषा',
    isoAssuranceComplete: 'ISO तैयारी जांच पूरी हुई। शेष अंतराल के लिए कार्य सूची देखें।',
    isoAssuranceFailed: 'ISO तैयारी जांच पूरी नहीं हो सकी। पुनः प्रयास करें या अपना इन्वेंटरी डेटा जांचें।',
    brsrDisclosureComplete: 'BRSR तैयारी जांच पूरी हुई। कार्य सूची में प्रकटीकरण अंतराल देखें।',
    brsrDisclosureFailed: 'BRSR तैयारी जांच पूरी नहीं हो सकी। पहले अपना कार्बन आकलन पूरा करें।',
    cbamReadinessComplete: 'CBAM निर्यात जांच पूरी हुई। इंस्टॉलेशन और दस्तावेज़ अंतराल देखें।',
    cbamReadinessFailed: 'CBAM निर्यात जांच पूरी नहीं हो सकी। अपना निर्यात प्रोफ़ाइल और बिल पुष्टि करें।',
    pcfOrchestrationFailed: 'उत्पाद फुटप्रिंट जांच पूरी नहीं हो सकी। उत्पाद नियम और लेनदेन जोड़ें।',
    exportHubHandoff: 'पूर्ण तैयारी जांच और प्राथमिकता वाली कार्य सूची के लिए Compliance Hub खोलें।',
    indiaHubHandoff: 'पूर्ण तैयारी जांच और प्राथमिकता वाली कार्य सूची के लिए Compliance Hub खोलें।',
    brsrScopeNotice: 'यह रिपोर्ट केवल BRSR Principle 6 (पर्यावरण) को कवर करती है — पूर्ण नौ-सिद्धांत BRSR Core filing नहीं। statutory filing से पहले सामाजिक और governance अनुभाग जोड़ें।',
    spendProxyWarning: 'कई लेनदेन अभी भी ₹-आधारित अनुमान का उपयोग करते हैं। assurance से पहले सटीकता बढ़ाने के लिए kWh, litre या kg वाले बिल अपलोड करें।',
    onboardingTitle: 'Compliance reporting में नए हैं?',
    onboardingSubtitle: 'ये तीन कदम अपनाएँ — अधिकांश MSME एक सत्र में बुनियादी तैयारी पूरी कर लेते हैं।',
    onboardingDismiss: 'समझ गया',
    onboardingStartAssessment: 'कार्बन आकलन शुरू करें',
    onboardingUploadBills: 'बिजली बिल अपलोड करें',
    onboardingSteps: [
      'बेसलाइन फुटप्रिंट',
      'बिल अपलोड',
      'तैयारी जांच चलाएँ',
    ],
    statutoryLockedHint: '{format} — {requirement} के बाद उपलब्ध होगा।',
    statutoryDownloadSuccess: '{format} सफलतापूर्वक डाउनलोड हुआ।',
    fixNextStep: 'अगला कदम पूरा करें',
    itemsLeftTitle: 'आधिकारिक फ़ाइलों के लिए {count} कदम बाकी',
    moreGapsTitle: 'अन्य पूर्ण करने वाले कदम',
    fixNow: 'अभी ठीक करें',
    readinessRequired: 'तैयारी आवश्यक',
    expertModeOn: 'विस्तृत दृश्य',
    expertModeOff: 'सरल दृश्य',
    expertModeHint: 'लेखा परीक्षकों, सलाहकारों या निर्यात अनुपालन टीमों के लिए।',
    matchBillsAutomatically: 'मेरे बिल स्वचालित रूप से मिलाएँ',
    checkReadinessFirst: 'पहले तैयारी जांचें',
    prepareAllReports: 'मेरी सभी रिपोर्ट तैयार करें',
    orchestrationTipTitle: 'एक बटन से भारत और निर्यात तैयारी जांचें',
    orchestrationTipSubtitle:
      'तैयारी बटन एक बार दबाएँ — हम बिल, BRSR फ़ील्ड, ISO सीमाएँ और CBAM निर्यात जांच एक साथ स्कैन करते हैं।',
    orchestrationTipDismiss: 'समझ गया',
    orchestrationTipSteps: [
      'मेरी रिपोर्टिंग तैयारी जांचें',
      'जिस मानक पर काम कर रहे हैं उसका गैप टैब खोलें',
      'संकेत मिलने पर बिलों से ऑटो-फ़िक्स करें',
    ],
  },
};

export const resolveReadinessLocale = (value?: string | null): ReadinessLocale => (
  value === 'hi' ? 'hi' : 'en'
);

export const getReadinessStrings = (locale: ReadinessLocale): ReadinessLocaleStrings => (
  READINESS_LOCALE_STRINGS[locale] || READINESS_LOCALE_STRINGS.en
);
